import React, { useRef, useEffect } from 'react'
import Box from '@mui/material/Box'
import lottie from 'lottie-web'

// Lottie animation URL from Firebase Storage
const LOTTIE_URL =
  'https://firebasestorage.googleapis.com/v0/b/gobeyond-app.appspot.com/o/app_art%2Fauth-animation.json?alt=media'

// Create a singleton Lottie animation that persists across route changes
let persistentLottieElement = null
let lottieContainer = null
let lottieAnimationData = null // cache for animation data

async function fetchLottieData() {
  if (lottieAnimationData) return lottieAnimationData
  const res = await fetch(LOTTIE_URL)

  lottieAnimationData = await res.json()

  return lottieAnimationData
}

async function createPersistentLottie() {
  if (persistentLottieElement) {
    return persistentLottieElement
  }

  // Create a container div for the Lottie animation
  const container = document.createElement('div')

  container.style.width = '100%'
  container.style.height = '100%'
  container.style.display = 'flex'
  container.style.justifyContent = 'center'
  container.style.alignItems = 'center'

  // Create the Lottie animation element
  const lottieElement = document.createElement('div')

  lottieElement.style.width = '100%'
  lottieElement.style.height = '100%'

  container.appendChild(lottieElement)

  // Fetch animation data from Firebase Storage
  const animationData = await fetchLottieData()

  // Initialize Lottie animation using the same library as react-lottie
  const anim = lottie.loadAnimation({
    container: lottieElement,
    renderer: 'svg',
    loop: true,
    autoplay: true,
    animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  })

  persistentLottieElement = container
  persistentLottieElement._animation = anim

  return container
}

function AuthLayout({ children }) {
  const lottieContainerRef = useRef(null)

  useEffect(() => {
    if (!lottieContainerRef.current) return

    let isMounted = true

    const initializeLottie = async () => {
      try {
        // Create or get the persistent Lottie element
        const lottieElement = await createPersistentLottie()

        if (!isMounted) return

        // If Lottie is not already in a container, add it to this container
        if (!lottieElement.parentElement) {
          lottieContainerRef.current.appendChild(lottieElement)
          lottieContainer = lottieContainerRef.current
        } else if (lottieElement.parentElement !== lottieContainerRef.current) {
          // If Lottie is in a different container, move it to this one
          lottieElement.parentElement.removeChild(lottieElement)
          lottieContainerRef.current.appendChild(lottieElement)
          lottieContainer = lottieContainerRef.current
        }
      } catch (error) {
        console.error('Failed to load Lottie animation:', error)
      }
    }

    initializeLottie()

    return () => {
      isMounted = false
    }
  }, [])

  return (
    <Box
      sx={{
        display: 'flex',
      }}
    >
      <Box
        sx={{
          width: {
            xs: '100%',
            sm: '60%',
            md: '50%',
          },
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {children}
      </Box>
      <Box
        ref={lottieContainerRef}
        sx={{
          width: { xs: 'none', sm: '40%', md: '50%' },
          height: '100vh',
          p: '30px',
          backgroundColor: 'white',
          display: {
            xs: 'none',
            sm: 'block',
          },
        }}
      />
    </Box>
  )
}

export default AuthLayout
